import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import {
  FiUser, FiMail, FiShield,
  FiArrowRight, FiChevronDown, FiHome, FiFlag,
} from 'react-icons/fi';
import { FaMars, FaVenus } from 'react-icons/fa';

import {
  RegisterPage, GameBg, PageBody,
  CentreCol,
  RegisterCard, ProfileBox, FieldGroup,
  SubmitBtn, CardFooter,
  ErrorText, GenderToggle,
} from './RegisterStyle';

import { register as authRegister } from '../../../store/auth/authSlice';
import { COUNTRIES } from '../../../data/countries';

const AVATARS = [
  { value: 'warrior_male',  label: 'Warrior male',   image: require('../../../assets/images/w1.png') },
  { value: 'warrior_female',  label: 'Warrior female',   image: require('../../../assets/images/w2.png') },

  { value: 'sura_male',     label: 'Sura male',      image: require('../../../assets/images/s1.png') },
  { value: 'sura_female',     label: 'Sura female',      image: require('../../../assets/images/s2.png') },

  { value: 'ninja_male',  label: 'Ninja Male',   image: require('../../../assets/images/n1.png') },
  { value: 'ninja_female',  label: 'Ninja female',   image: require('../../../assets/images/n2.png') },

  { value: 'shaman_male',   label: 'Shaman male',    image: require('../../../assets/images/sh1.png') },
  { value: 'shaman_female',   label: 'Shaman female',    image: require('../../../assets/images/sh2.png') },

  { value: 'lycan_male',    label: 'Lycan male',     image: require('../../../assets/images/l.png') },
];

const Register = () => {
  const { t } = useTranslation();
  const toast = useToast({ position: 'top', duration: 5000, status: 'error' });
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);

  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: { avatar: 'warrior_male', country: '' },
  });

  const [selectedAvatar, setSelectedAvatar] = useState(AVATARS[0]);
  const [gender, setGender] = useState('male');

  const countryValue = watch('country');
  const selectedCountry = COUNTRIES.find(c => c.code === countryValue);

  const genderAvatars = AVATARS.filter(a => a.value.endsWith(`_${gender}`));

  const handleGenderChange = (newGender) => {
    setGender(newGender);
    const currentClass = selectedAvatar.value.replace(/_male$|_female$/, '');
    const sameClass = AVATARS.find(a => a.value === `${currentClass}_${newGender}`);
    const next = sameClass || AVATARS.find(a => a.value.endsWith(`_${newGender}`));
    if (next) setSelectedAvatar(next);
  };
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState('idle'); // idle | sending | sent | error
  const cooldownRef = useRef(null);

  useEffect(() => {
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  const handleResend = async () => {
    if (resendCooldown > 0 || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      await axios.post('/api/resend_verification', { email: registeredEmail });
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const onSubmit = values => {
    dispatch(authRegister({
      name: values.nickname,
      email: values.email,
      password: values.password,
      avatar: selectedAvatar.value,
      gender,
      country: values.country || null,
    }))
      .unwrap()
      .then(() => { setRegisteredEmail(values.email); setRegistered(true); })
      .catch(error => {
        toast({ description: error.errors?.[0]?.msg || 'Registration failed' });
      });
  };

  if (registered) {
    return (
      <RegisterPage>
        <GameBg>
          <PageBody>
            <CentreCol>
              <RegisterCard>
                <div className="card-top-icon" style={{ fontSize: 24 }}>✉️</div>
                <h2 className="card-title">تحقق من بريدك الإلكتروني</h2>
                <p className="card-subtitle">
                  تم إنشاء حسابك بنجاح! أرسلنا رسالة تفعيل إلى <strong style={{ color: '#c8a040' }}>{registeredEmail}</strong>.
                  <br />انقر على الرابط في الرسالة لتفعيل حسابك.
                </p>

                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || resendStatus === 'sending'}
                  style={{
                    display: 'block', width: '100%', padding: '11px',
                    background: resendCooldown > 0 ? '#2a2a2a' : 'linear-gradient(90deg,#c0392b,#e74c3c)',
                    color: resendCooldown > 0 ? '#666' : '#fff',
                    border: 'none', borderRadius: 8, fontWeight: 700,
                    fontSize: 14, cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
                    marginBottom: 12, transition: 'all .2s',
                  }}
                >
                  {resendStatus === 'sending'
                    ? 'جارٍ الإرسال…'
                    : resendCooldown > 0
                      ? `إعادة الإرسال (${resendCooldown}ث)`
                      : resendStatus === 'sent'
                        ? '✓ تم الإرسال — إعادة الإرسال'
                        : resendStatus === 'error'
                          ? 'فشل الإرسال — حاول مرة أخرى'
                          : 'إعادة إرسال رسالة التفعيل'
                  }
                </button>

                <Link to="/login" className="signin-link" style={{ display: 'flex', justifyContent: 'center', color: '#c8a040', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
                  العودة إلى تسجيل الدخول
                </Link>

                <div style={{ textAlign: 'center', marginTop: 10 }}>
                  <Link to="/" style={{ color: '#555', fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <FiHome size={12} /> العودة إلى الصفحة الرئيسية
                  </Link>
                </div>
              </RegisterCard>
            </CentreCol>
          </PageBody>
        </GameBg>
      </RegisterPage>
    );
  }

  return (
    <RegisterPage>
      {/* ── GAME BACKGROUND ── */}
      <GameBg>
        <PageBody>
          <CentreCol>

            {/* Register card */}
            <RegisterCard>
              {/* Top icon */}
              <div className="card-top-icon">+</div>

              <h2 className="card-title">{t('website.register.title')}</h2>
              <p className="card-subtitle">{t('website.register.subtitle')}</p>

              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Profile box: avatar + nickname + gender toggle + avatar select */}
                <ProfileBox>
                  <div
                    className="avatar-circle"
                    title={selectedAvatar.label}
                    style={{
                      borderColor: gender === 'male' ? 'rgba(96,165,250,0.55)' : 'rgba(244,114,182,0.55)',
                      boxShadow: gender === 'male' ? '0 0 14px rgba(96,165,250,0.18)' : '0 0 14px rgba(244,114,182,0.18)',
                    }}
                  >
                    <img src={selectedAvatar.image} alt={selectedAvatar.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>

                  <div className="profile-fields">
                    {/* Nickname */}
                    <FieldGroup style={{ marginBottom: 0 }}>
                      <span className="field-label">{t('website.register.nickname')}</span>
                      <div className="field-wrap">
                        <FiUser className="field-icon" />
                        <input
                          type="text"
                          placeholder={t('website.register.nickname_placeholder')}
                          {...register('nickname', { required: t('validation.required') })}
                        />
                      </div>
                      {errors.nickname && <ErrorText>{errors.nickname.message}</ErrorText>}
                    </FieldGroup>

                    {/* Gender toggle */}
                    <GenderToggle>
                      <button
                        type="button"
                        className={gender === 'male' ? 'male-active' : ''}
                        onClick={() => handleGenderChange('male')}
                      >
                        <FaMars /> {t('website.register.gender_male')}
                      </button>
                      <button
                        type="button"
                        className={gender === 'female' ? 'female-active' : ''}
                        onClick={() => handleGenderChange('female')}
                      >
                        <FaVenus /> {t('website.register.gender_female')}
                      </button>
                    </GenderToggle>

                    {/* Avatar select — filtered by gender */}
                    <FieldGroup style={{ marginBottom: 0 }}>
                      <span className="field-label">{t('website.register.avatar')}</span>
                      <div className="field-wrap">
                        <FiShield className="field-icon" />
                        <select
                          value={selectedAvatar.value}
                          onChange={e => {
                            const found = genderAvatars.find(a => a.value === e.target.value);
                            if (found) setSelectedAvatar(found);
                          }}
                        >
                          {genderAvatars.map(a => (
                            <option key={a.value} value={a.value}>{a.label}</option>
                          ))}
                        </select>
                        <FiChevronDown className="select-arrow" />
                      </div>
                    </FieldGroup>
                  </div>
                </ProfileBox>

                {/* E-mail */}
                <FieldGroup>
                  <span className="field-label">{t('website.register.email')}</span>
                  <div className="field-wrap">
                    <FiMail className="field-icon" />
                    <input
                      type="email"
                      placeholder="you@email.com"
                      {...register('email', {
                        required: t('validation.required'),
                        pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' },
                      })}
                    />
                  </div>
                  {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
                </FieldGroup>

                {/* Password */}
                <FieldGroup>
                  <span className="field-label">{t('website.register.password')}</span>
                  <div className="field-wrap">
                    <FiShield className="field-icon" />
                    <input
                      type="password"
                      placeholder="••••••••"
                      {...register('password', {
                        required: t('validation.required'),
                        minLength: { value: 8, message: t('validation.min_length') + ' 8' },
                      })}
                    />
                  </div>
                  {errors.password && <ErrorText>{errors.password.message}</ErrorText>}
                </FieldGroup>

                {/* Country */}
                <FieldGroup>
                  <span className="field-label">{t('website.register.country')}</span>
                  <div className="field-wrap">
                    {selectedCountry
                      ? <img src={selectedCountry.flag} alt={selectedCountry.name} className="field-icon" style={{ width: 16, height: 16, objectFit: 'cover', borderRadius: 3 }} />
                      : <FiFlag className="field-icon" />
                    }
                    <select
                      defaultValue=""
                      {...register('country', { required: t('validation.required') })}
                    >
                      <option value="" disabled>{t('website.register.country_placeholder')}</option>
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                    <FiChevronDown className="select-arrow" />
                  </div>
                  {errors.country && <ErrorText>{errors.country.message}</ErrorText>}
                </FieldGroup>

                {/* Fake reCAPTCHA */}
                {/* <CaptchaBox>
                    <div
                      className={`captcha-check ${captchaChecked ? 'checked' : ''}`}
                      onClick={() => setCaptchaChecked(v => !v)}
                    >
                      {captchaChecked && '✓'}
                    </div>
                    <span className="captcha-label">{t('website.register.not_robot')}</span>
                    <div className="captcha-logo">
                      <FiRefreshCw className="logo-icon" />
                      <span className="logo-text">M2<br />Verify</span>
                    </div>
                  </CaptchaBox> */}

                {/* Submit */}
                <SubmitBtn type="submit" disabled={auth.isLoading}>
                  {auth.isLoading
                    ? t('website.register.registering')
                    : <>{t('website.register.register_btn')} <FiArrowRight size={16} /></>
                  }
                </SubmitBtn>

                {/* Sign in link */}
                <CardFooter>
                  <span className="footer-text">{t('website.register.have_account')}</span>
                  <Link to="/login" className="signin-link">
                    {t('website.register.sign_in')} <FiArrowRight size={12} />
                  </Link>
                </CardFooter>

                <div style={{ textAlign: 'center', marginTop: 8 }}>
                  <Link to="/" style={{ color: '#555', fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <FiHome size={12} /> العودة إلى الصفحة الرئيسية
                  </Link>
                </div>
              </form>
            </RegisterCard>
          </CentreCol>
        </PageBody>
      </GameBg>
    </RegisterPage>
  );
};

export default Register;
